package io.horion.cv.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.icons.fa.FaDownload
import com.varabyte.kobweb.silk.components.icons.fa.FaHouse
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import io.horion.cv.api.datasource.payement.PaymentMethod
import io.horion.cv.components.Footer
import io.horion.cv.components.Topbar
import io.horion.cv.di.koinGet
import io.horion.cv.pages.navigation.Screen
import io.horion.cv.styles.*
import io.horion.cv.utils.Colors
import io.horion.cv.utils.Constants.INTER
import io.horion.cv.viewModel.PayViewModel
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Text

fun generateTransactionId(): String {
    val chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"
    val part1 = (1..2).map { chars.random() }.joinToString("")
    val part2 = (1..2).map { ('0'..'9').random() }.joinToString("")
    val part3 = (1..1).map { chars.first { it.isLetter() } }.joinToString("")
    val part4 = (1..5).map { ('0'..'9').random() }.joinToString("")
    return "$part1 $part2 $part3-$part4"
}

fun getCurrentDateTime(): Pair<String, String> {
    val now = js("new Date()")
    val day = now.getDate().toString().unsafeCast<String>().padStart(2, '0')
    val month = (now.getMonth() + 1).toString().unsafeCast<String>().padStart(2, '0')
    val year = now.getFullYear().toString().unsafeCast<String>()
    val hours = now.getHours().toString().unsafeCast<String>().padStart(2, '0')
    val minutes = now.getMinutes().toString().unsafeCast<String>().padStart(2, '0')
    return Pair("$day-$month-$year", "$hours:$minutes")
}

@Page("/payment-success")
@Composable
fun PaymentSuccessPage() {
    val ctx = rememberPageContext()
    val payViewModel = koinGet<PayViewModel>()
    val payUiState = payViewModel.paymentUiState

    LaunchedEffect(Unit){
        if (payUiState.phone.isEmpty()){
            ctx.router.navigateTo(Screen.Payment.path)
        }
    }

    val isOrange = payUiState.method == PaymentMethod.OM
    val transactionId = remember { generateTransactionId() }
    val (date, time) = remember { getCurrentDateTime() }

    val colorMode by ColorMode.currentState
    val isDark = colorMode.isDark
    val pageBg     = if (isDark) Colors.DarkBG    else Colors.White
    val titleColor = if (isDark) Colors.White     else Colors.DarkBlue
    val subtitleColor = if (isDark) Colors.LightGray else Colors.Primary


    Column(
        modifier = Modifier
            .fillMaxWidth()
            .backgroundColor(pageBg)
            .styleModifier { property("transition", "background-color 300ms ease") },
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Topbar()

        Column(
            modifier = SuccessContainerStyle.toModifier(),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            SpanText(
                text = "Paiement validé",
                modifier = SuccessTitleStyle.toModifier()
                    .fontWeight(FontWeight.Bold)
                    .fontFamily(INTER)
                    .color(titleColor)
            )

            SpanText(
                text = "Votre paiement a été traité avec succès",
                modifier = SuccessSubtitleStyle.toModifier()
                    .fontFamily(INTER)
                    .fontWeight(FontWeight.SemiBold)
                    .color(subtitleColor)
                    .styleModifier { property("text-align", "center") }
            )

            val colorMode by ColorMode.currentState
            val isDark = colorMode.isDark
            val cardBg      = if (isDark) Colors.holder    else Colors.PriceGray
            val cardBorder  = if (isDark) Colors.holder    else Colors.DarkBlue


            Column(
                modifier = SuccessCardContainerStyle.toModifier()
                    .backgroundColor(cardBg)
                    .styleModifier { property("transition", "background-color 300ms ease") }
                    .borderRadius(32.px)
                    .border(0.3.px, LineStyle.Solid, cardBorder),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                SpanText(
                    text = "Détails de la transaction",
                    modifier = SuccessCardTitleStyle.toModifier()
                        .fontFamily(INTER)
                        .fontWeight(FontWeight.Medium)
                        .color(if (isDark) Colors.LightGray else Colors.Primary)
                )

                TransactionRow(
                    label = "Numero telephone",
                    value = payUiState.phone,
                )

                TransactionRow(
                    label = "Montant payé",
                    value = "XAF ${payUiState.amount}",
                )

                TransactionRow(
                    label = "Date et heure",
                    valueComposable = {
                        Row(
                            horizontalArrangement = Arrangement.End,
                            verticalAlignment = Alignment.CenterVertically,
                            modifier = TransactionDateGapStyle.toModifier()
                        ) {
                            SpanText(
                                text = date,
                                modifier = TransactionTextResponsiveStyle.toModifier()
                                    .fontFamily(INTER)
                                    .fontWeight(FontWeight.SemiBold)
                                    .color(if (isDark) Colors.White else Colors.Black)
                            )
                            SpanText(
                                text = time,
                                modifier = TransactionTextResponsiveStyle.toModifier()
                                    .fontFamily(INTER)
                                    .fontWeight(FontWeight.SemiBold)
                                    .color(if (isDark) Colors.LightGray else Colors.Primary)
                            )
                        }
                    },
                    isLast = false
                )

                TransactionRow(
                    label = "Mode de paiement",
                    valueComposable = {
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            modifier = TransactionPaymentModeGapStyle.toModifier()
                        ) {
                            SpanText(
                                text = payUiState.phone,
                                modifier = TransactionPhoneTextStyle.toModifier()
                                    .fontFamily(INTER)
                                    .fontWeight(FontWeight.SemiBold)
                                    .color(if (isDark) Colors.LightGray else Colors.Primary)
                            )
                            Image(
                                src = if (isOrange) "/orange.png" else "/mtn.png",
                                description = if (isOrange) "Orange Money" else "MTN Mobile Money",
                                modifier = TransactionProviderImageStyle.toModifier()
                                    .objectFit(ObjectFit.Contain)
                                    .styleModifier {
                                        property("filter", if (isDark) "brightness(1.1)" else "none")
                                    }
                            )
                        }
                    },
                    isLast = true
                )
            }

            Button(
                onClick = {
                    payViewModel.downloadCv()
                     },
                modifier = SubscribeButtonStyle.toModifier()
                    .then(SuccessActionButtonStyle.toModifier())
                    .borderRadius(30.px)
                    .fontFamily(INTER)
                    .fontWeight(FontWeight.Medium)
                    .color(Colors.White)

            ) {

                FaDownload(
                    modifier = SuccessIconStyle.toModifier()
                )
                Text("  Télécharger")
            }

            Box(Modifier.height(20.px))

            Button(
                onClick = {
                    ctx.router.navigateTo(Screen.Home.path)
                },
                modifier = validatebuttonStyle.toModifier()
                    .then(SuccessSecondaryButtonStyle.toModifier())
                    .borderRadius(30.px)
                    .fontFamily(INTER)
                    .fontWeight(FontWeight.Medium)
                    .color(Colors.White)
                    .styleModifier { property("box-shadow", "none") }
            ) {

                FaHouse(
                    modifier = SuccessIconStyle.toModifier()
                )
                Text("  Retour à l'accueil")
            }
        }

        Footer()
    }
}

@Composable
fun TransactionRow(
    label: String,
    value: String = "",
    valueComposable: (@Composable () -> Unit)? = null,
    isLast: Boolean = false
) {
    val colorMode by ColorMode.currentState
    val isDark = colorMode.isDark
    val labelColor     = if (isDark) Colors.LightGray else Colors.Primary
    val valueColor     = if (isDark) Colors.White     else Colors.Primary
    val separatorColor = if (isDark) Colors.holder    else Colors.DarkGray

    Column(
        modifier = Modifier.fillMaxWidth()
    ) {
        Row(
            modifier = TransactionRowPaddingStyle.toModifier().fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                text = label,
                modifier = TransactionTextResponsiveStyle.toModifier()
                    .fontFamily(INTER)
                    .fontWeight(FontWeight.Medium)
                    .color(labelColor)
            )

            if (valueComposable != null) {
                valueComposable()
            } else {
                SpanText(
                    text = value,
                    modifier = TransactionTextResponsiveStyle.toModifier()
                        .fontFamily(INTER)
                        .fontWeight(FontWeight.SemiBold)
                        .color(valueColor)
                )
            }
        }

        if (!isLast) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(1.px)
                    .backgroundColor(separatorColor)
            ) {}
        }
    }
}
